import { useEffect, useState } from 'react';
import { Input } from '@/components/ui/input';
import { FieldExplainer } from '@/components/calculator/FieldExplainer';
import type { FieldExplainerContent } from '@/content/field-explainers/types';

type NumberFieldProps = {
  id: string;
  label: string;
  value: number;
  min?: number;
  max?: number;
  step?: number;
  helperText?: string;
  prefix?: string;
  suffix?: string;
  parseMode?: 'integer' | 'decimal';
  allowNegative?: boolean;
  explainer?: FieldExplainerContent;
  onChange: (next: number) => void;
};

function sanitizeDraftValue(rawValue: string, parseMode: 'integer' | 'decimal', allowNegative: boolean): string {
  let draft = rawValue.replace(/[^\d.\-]/g, '');
  if (!allowNegative) {
    draft = draft.replace(/-/g, '');
  } else {
    draft = draft.replace(/-/g, '');
    if (rawValue.trim().startsWith('-')) {
      draft = `-${draft}`;
    }
  }

  if (parseMode === 'integer') {
    draft = draft.replace(/\./g, '');
    return draft;
  }

  const dotIndex = draft.indexOf('.');
  if (dotIndex === -1) {
    return draft;
  }

  return `${draft.slice(0, dotIndex + 1)}${draft.slice(dotIndex + 1).replace(/\./g, '')}`;
}

function isTransientDraftValue(value: string): boolean {
  return value === '' || value === '-' || value === '.' || value === '-.';
}

function clampValue(value: number, min?: number, max?: number): number {
  if (typeof min === 'number') {
    value = Math.max(value, min);
  }
  if (typeof max === 'number') {
    value = Math.min(value, max);
  }
  return value;
}

export function NumberField({
  id,
  label,
  value,
  min,
  max,
  step = 1,
  helperText,
  prefix,
  suffix,
  parseMode = 'decimal',
  allowNegative = false,
  explainer,
  onChange
}: NumberFieldProps) {
  const toDisplayValue = (nextValue: number) => (Number.isFinite(nextValue) ? String(nextValue) : '');
  const [draftValue, setDraftValue] = useState(() => toDisplayValue(value));

  useEffect(() => {
    setDraftValue(toDisplayValue(value));
  }, [value]);

  return (
    <div className="space-y-2 text-sm">
      <span className="flex items-center gap-1.5">
        <label htmlFor={id} className="text-sm font-semibold text-foreground">
          {label}
        </label>
        {explainer ? <FieldExplainer explainer={explainer} /> : null}
      </span>
      <div className="relative">
        {prefix ? <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">{prefix}</span> : null}
        <Input
          id={id}
          type="text"
          inputMode={parseMode === 'integer' ? 'numeric' : 'decimal'}
          value={draftValue}
          onChange={(event) => {
            const nextDraftValue = sanitizeDraftValue(event.target.value, parseMode, allowNegative);
            setDraftValue(nextDraftValue);

            if (isTransientDraftValue(nextDraftValue)) {
              return;
            }

            const nextValue = Number(nextDraftValue);
            if (Number.isFinite(nextValue)) {
              onChange(nextValue);
            }
          }}
          onBlur={() => {
            if (isTransientDraftValue(draftValue)) {
              setDraftValue(toDisplayValue(value));
              return;
            }

            const parsedValue = Number(draftValue);
            if (!Number.isFinite(parsedValue)) {
              setDraftValue(toDisplayValue(value));
              return;
            }

            const clamped = clampValue(parsedValue, min, max);
            const normalized = parseMode === 'integer' ? Math.round(clamped) : clamped;
            onChange(normalized);
            setDraftValue(toDisplayValue(normalized));
          }}
          className={`${prefix ? 'pl-7' : ''} ${suffix ? 'pr-9' : ''}`}
          aria-label={label}
        />
        {suffix ? <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">{suffix}</span> : null}
      </div>
      {helperText ? <span className="block text-xs text-muted-foreground">{helperText}</span> : null}
    </div>
  );
}
